import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Download, GitBranch, RefreshCcw } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { n: 1, title: "Alta de proveedores", role: "Administrador", desc: "El Administrador registra proveedores: alta individual o carga masiva CSV (SAP ID, RFC, categoría y comprador)." },
  { n: 2, title: "Asignación por pilar", role: "Administrador", desc: "El Administrador asigna a cada evaluador su(s) pilar(es) y sus permisos (ver, descargar, editar) en Usuarios y permisos." },
  { n: 3, title: "Captura mensual", role: "Evaluador", desc: "Cada evaluador captura Objetivo y Real (nivel 1–4) solo de su pilar, según la periodicidad de cada indicador." },
  { n: 4, title: "Consolidación del score", role: "Plataforma", desc: "La plataforma promedia los niveles por pilar y pondera los 6 pilares para obtener el score 0–10." },
  { n: 5, title: "Clasificación por nivel", role: "Plataforma", desc: "El nivel se determina con las reglas completas de reconocimiento: promedio, mínimos por pilar y condiciones de cumplimiento. Rangos visibles: Platino 9.5–10 · Oro 8.5–9.4 · Plata 7.5–8.4 · Bronce 5.8–7.4 · Recuperación < 5.8." },
  { n: 6, title: "Sello + Reporte ejecutivo", role: "Proveedor", desc: "Se genera el sello digital y el reporte PDF. El proveedor consulta su desempeño en modo lectura." },
  { n: 7, title: "Retroalimentación y mejora", role: "Todos", desc: "Planes de acción, centro de aprendizaje y seguimiento de brechas. El ciclo se repite cada mes." },
];

const LANES = [
  {
    role: "Administrador", color: "#0033AD",
    acts: ["Da de alta proveedores (CSV o individual)", "Asigna pilares y permisos a evaluadores", "Monitorea completitud del mes (heatmap)", "Cierra el periodo y comunica resultados"],
  },
  {
    role: "Evaluador por pilar", color: "#2A8A3E",
    acts: ["Recibe su pilar asignado (p. ej. Innovación)", "Captura mensual: Objetivo vs Real (1–4)", "Registra notas por indicador", "Da seguimiento a brechas (gaps)"],
  },
  {
    role: "Proveedor (solo lectura)", color: "#B87333",
    acts: ["Consulta su score y nivel (Platino–Bronce)", "Descarga su sello digital y reporte PDF", "Accede al centro de aprendizaje"],
  },
  {
    role: "Stakeholder / Dirección", color: "#D4A437",
    acts: ["Consulta dashboards y rankings", "Revisa benchmarking por categoría", "Solicita ajustes o cambios al Administrador"],
  },
];

const TIERS = [
  { label: "Platino 9.5–10", color: "#0033AD" },
  { label: "Oro 8.5–9.4", color: "#D4A437" },
  { label: "Plata 7.5–8.4", color: "#94A3B8" },
  { label: "Bronce 5.8–7.4", color: "#B87333" },
  { label: "Recuperación < 5.8", color: "#E0162B" },
];

export default function FlujoPrograma() {
  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="flujo-programa-page">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <Link to="/" className="text-sm text-[var(--lala-blue)] hover:underline inline-flex items-center gap-1 mb-2">
            <ArrowLeft size={14} /> Volver
          </Link>
          <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2">
            <GitBranch size={18} /> diagrama de flujo
          </div>
          <h1 className="font-display text-4xl sm:text-5xl">Flujo del Programa LALA Elite</h1>
          <p className="text-[var(--ink-soft)] mt-2 max-w-2xl">
            El proceso completo de la evaluación 360° de proveedores — end-to-end y por rol.
            Ideal para presentaciones y juntas de seguimiento.
          </p>
        </div>
        <a href={`${API_BASE}/api/public/flujo-proceso`}
          data-testid="flujo-download-pdf"
          className="sketch-pill bg-[var(--lala-blue)] text-white px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 hover:opacity-90 shrink-0">
          <Download size={14} /> Descargar diagrama PDF
        </a>
      </div>

      {/* Proceso end-to-end */}
      <div className="sketch-border bg-white p-6 mb-10" data-testid="flujo-proceso-section">
        <div className="font-hand text-xl text-[var(--lala-blue)]">proceso end-to-end</div>
        <h2 className="font-display text-lg mb-1 text-[var(--lala-blue)]">Un ciclo mensual de 7 pasos</h2>
        <div className="text-xs text-emerald-700 font-semibold inline-flex items-center gap-1 mb-5">
          <RefreshCcw size={12} /> Ciclo mensual continuo — cada periodo se vuelve a capturar
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {STEPS.map((s, i) => (
            <div key={s.n} className="relative card-silver p-4"
              data-testid={`flujo-step-${s.n}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-7 h-7 rounded-full bg-[var(--lala-blue)] text-white text-sm font-bold flex items-center justify-center shrink-0">{s.n}</span>
                <div className="font-display text-base leading-tight">{s.title}</div>
              </div>
              <p className="text-xs text-[var(--ink-soft)] leading-relaxed">{s.desc}</p>
              <span className="inline-block mt-3 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[var(--lala-blue-soft)] text-[var(--lala-blue)]">{s.role}</span>
              {i < STEPS.length - 1 && (
                <ArrowRight size={16}
                  className={`${i % 4 === 3 ? "hidden" : "hidden lg:block"} absolute z-10 -right-4 top-1/2 -translate-y-1/2 text-[var(--lala-blue)]`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-5 mt-6 pt-4 border-t border-[var(--line)]">
          <span className="text-xs font-bold text-[var(--ink)]">Niveles del programa:</span>
          {TIERS.map((t) => (
            <span key={t.label} className="inline-flex items-center gap-1.5 text-xs text-[var(--ink-soft)]">
              <span className="w-3 h-3 rounded-full inline-block" style={{ background: t.color }} />
              {t.label}
            </span>
          ))}
        </div>
      </div>

      {/* Flujo por rol */}
      <div className="sketch-border bg-white p-6" data-testid="flujo-roles-section">
        <div className="font-hand text-xl text-[var(--lala-blue)]">flujo por rol</div>
        <h2 className="font-display text-lg mb-5 text-[var(--lala-blue)]">Quién hace qué dentro del programa</h2>
        <div className="space-y-4">
          {LANES.map((lane) => (
            <div key={lane.role} className="bg-white rounded-xl border border-[var(--line)] overflow-hidden"
              data-testid={`flujo-lane-${lane.role.split(" ")[0].toLowerCase()}`}>
              <div className="px-4 py-2 text-white text-sm font-bold" style={{ background: lane.color }}>
                {lane.role}
              </div>
              <div className="flex flex-wrap items-stretch gap-2 p-4">
                {lane.acts.map((act, i) => (
                  <div key={act} className="flex items-center gap-2">
                    <div className="rounded-lg border px-3 py-2 text-xs max-w-[210px] leading-snug"
                      style={{ borderColor: lane.color }}>
                      {act}
                    </div>
                    {i < lane.acts.length - 1 && <ArrowRight size={14} style={{ color: lane.color }} className="shrink-0" />}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
